import { useAppStore } from '@/store';
import { request } from '@/utils/request';

/* 获取项目配置 */
export function getProjectConfig(url: string) {
  const BaseUrl = import.meta.env.VITE_SERVER_BASEURL || '';
  return request<API.GetInfo>({
    url: `${BaseUrl}/newapi/site/site/getInfo`,
    method: 'POST',
    data: {
      url,
    },
  }, {
    cache: true,
    cacheTime: 60,
  });
}

/* 获取设备IP */
export function getIp() {
  const BaseUrl = import.meta.env.VITE_SERVER_BASEURL || '';
  return request<{ ip: string }>({
    url: `${BaseUrl}/newapi/client/getClientInfo`,
    method: 'post',
  });
}

/* 获取项目的服务配�? */
export function getProjectServices() {
  return request({
    url: '/newapi/site/site/getServiceList',
    method: 'GET',
  });
}

/* 获取全量站点列表（新接口，仅返回站点数组，已 status 过滤后由 store 处理�? */
export function getSiteList(data: Record<string, any> = {}, siteCode: string) {
  return request<Record<string, API.Site> | API.Site[]>({
    url: '/newapi/site/site/getSiteList',
    method: 'GET',
    headers: {
      site: siteCode,
    },
    data,
  }).then((res) => {
    // 后端返回为以数字�?key 的对象，统一归一化为数组
    if (Array.isArray(res)) return res;
    return res ? Object.values(res) : [];
  });
}

/* 获取站点分组选项（用于快速切换站点弹框，按需调用�? */
export function getSiteOptions(data: Record<string, any> = {}, siteCode: string) {
  return request<Record<string, API.SiteOptionGroup> | API.SiteOptionGroup[]>({
    url: '/newapi/site/site/getSiteOptions',
    method: 'GET',
    headers: {
      site: siteCode,
    },
    data,
  }).then((res) => {
    if (Array.isArray(res)) return res;
    return res ? Object.values(res) : [];
  });
}

/**
 * 获取平台列表
 * dfType: 订单类型 代发类型1通用 2关联�?3手工�?
 * type: 类型 0全部 1分组
 * isCopyGoods: 是否复制商品
 * isSellerUpGoods: isSellerUpGoods
 */
export function getPlatformList(data: {
  dfType?: number
  isCopyGoods?: 0 | 1
  isSellerUpGoods?: 0 | 1
  type?: number
} = {}) {
  return request<API.PlatformItem[]>({
    url: '/newapi/platform/getPtList',
    method: 'get',
    data,
  });
}

/** 获取退货地址列表 */
export function getReturnAddressList() {
  return request<API.ReturnAddressItem[]>({
    url: '/newapi/site/site/getRefundAddressList',
    method: 'GET',
  });
}

/** 获取省市区列�? */
export function getRegionList() {
  return request<Record<string, any>>({
    url: '/newapi/order/logistics/getRegionTreeList',
    method: 'GET',
  });
}

/** 分类树节点（新接口字段 → 旧接口字段映射） */
interface CategoryTreeNode {
  id: number
  title: string
  image: string
  is_last: number
  children?: CategoryTreeNode[]
  /** 新接口原始字段，透传供需要时使用 */
  cateLv?: number | null
  catePath?: string
  disabled?: boolean
}

/** 递归将新接口字段映射为旧接口字段（cateName→title, isLast→is_last） */
function mapCategoryTree(nodes: any[]): CategoryTreeNode[] {
  return nodes.map((node) => {
    const mapped: CategoryTreeNode = {
      id: node.id,
      title: node.cateName ?? node.title ?? '',
      image: node.image ?? '',
      is_last: node.isLast ?? node.is_last ?? 0,
      cateLv: node.cateLv ?? null,
      catePath: node.catePath ?? '',
      disabled: node.disabled ?? false,
    };
    if (node.children?.length) {
      mapped.children = mapCategoryTree(node.children);
    }
    return mapped;
  });
}

/** 获取站点分类列表 */
export function loadSiteCategory(siteCode: string) {
  return request({
    url: '/newapi/goods/category/getSiteCategoryTree',
    method: 'GET',
    headers: {
      site: siteCode,
    },
    data: {},
  }).then((res: any) => {
    const data = res?.data || res || [];
    const list = Array.isArray(data) ? data : Object.values(data);
    return { ...res, data: mapCategoryTree(list) };
  });
}

/** APP下载信息 */
export interface AppDownloadInfo {
  iosLink: string
  androidLink: string
}

/** 获取APP下载信息 */
export function getAppInfo(apiUrl: string) {
  return request<AppDownloadInfo>({
    url: `${apiUrl}/newapi/content/getAppInfo`,
    method: 'GET',
  });
}

/**
 * 上传本地图片到服务器临时图片库（图片服务�?api�?
 * 对应老项�?uni.$api.utils.uploadTempImage �?config.getImageApi('saveKfTmpFile')
 * @param filePath 本地文件路径（App/小程序临时路径；H5 支持 blob/data URL�?
 * @returns 临时图片 URL
 */
export function uploadTempImage(filePath: string): Promise<string> {
  const appStore = useAppStore();
  const uploadUrl = `${appStore.imgApiUrl || import.meta.env.VITE_SERVER_BASEURL || ''}/saveKfTmpFile`;

  // 解析上传响应，取出临时图片地址
  const parseUrl = (raw: any): string => {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const data = parsed?.data || parsed;
    return typeof data === 'string' ? data : (data?.file_path || data?.url || data?.path || '');
  };

  // #ifdef H5
  // H5 端裁剪组件返�?data URL，uni.uploadFile 不支持，�?fetch + FormData
  if (filePath.startsWith('data:')) {
    return fetch(filePath)
      .then(res => res.blob())
      .then((blob) => {
        const formData = new FormData();
        formData.append('file_upload', blob, `search_${Date.now()}.jpg`);
        return fetch(uploadUrl, { method: 'POST', body: formData });
      })
      .then(res => res.json())
      .then((res) => {
        const url = parseUrl(res);
        if (!url) throw new Error('上传响应缺少文件地址');
        return url;
      });
  }
  // #endif

  return new Promise((resolve, reject) => {
    uni.uploadFile({
      url: uploadUrl,
      filePath,
      name: 'file_upload',
      success: (res) => {
        try {
          const url = parseUrl(res.data);
          if (url) resolve(url);
          else reject(new Error('上传响应缺少文件地址'));
        }
        catch {
          reject(new Error('上传响应解析失败'));
        }
      },
      fail: reject,
    });
  });
}
